import {
  buildZodSchemaFromConfig,
  inferInputTypeFromComponentId,
  type ValidationConfig,
  type ValidationInputType,
} from "#bb/lib/validation";
import { eventBus } from "#bb/lib/eventBus";

export interface FormContext {
  id: string;
  registerField: (id: string, validator: () => any) => void;
  unregisterField: (id: string) => void;
  setFieldError: (id: string, message: string | null) => void;
  validateAll: () => Promise<boolean>;
  errors: { value: Record<string, string> } | any;
}

export interface UseFormFieldOptions {
  id: string;
  componentId: string;
  getValue: () => unknown;
  getValidation: () => ValidationConfig | undefined;
  /** Override the input type inferred from componentId. */
  inputType?: ValidationInputType;
}

/**
 * Wires an assessment field into the surrounding form-container (if any).
 * Returns a reactive `error` ref the renderer can show under the input,
 * and `validate` helpers it should call on blur / change.
 */
export function useFormField(options: UseFormFieldOptions) {
  const formContext = inject<FormContext | null>("form-context", null);
  const error = ref<string>("");

  const inputType = options.inputType ?? inferInputTypeFromComponentId(options.componentId);

  function runValidation(): { ok: true } | { ok: false; message: string } {
    const schema = buildZodSchemaFromConfig(options.getValidation(), inputType);
    const result = schema.safeParse(options.getValue());
    if (result.success) {
      error.value = "";
      formContext?.setFieldError(options.id, null);
      return { ok: true };
    }
    const message = result.error.issues[0]?.message || "Invalid value";
    error.value = message;
    formContext?.setFieldError(options.id, message);
    return { ok: false, message };
  }

  function clearError() {
    error.value = "";
    formContext?.setFieldError(options.id, null);
  }

  onMounted(() => {
    formContext?.registerField(options.id, runValidation);
  });

  onBeforeUnmount(() => {
    formContext?.unregisterField(options.id);
  });

  // Reflect form-level error state if the form pushes errors to us.
  if (formContext) {
    watch(
      () => (formContext.errors?.value || formContext.errors)?.[options.id],
      (msg: string | undefined) => {
        error.value = msg || "";
      }
    );
  }

  // Allow the renderer to re-validate when answers change elsewhere.
  eventBus.on(`component:revalidate:${options.id}`, () => {
    runValidation();
  });

  return {
    error,
    validate: runValidation,
    clearError,
    isInForm: computed(() => !!formContext),
  };
}
